import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { useApiQuery } from '../useApiQuery';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import { contactKeys } from '@/lib/queryKeys';
import type { ContactFileAttachment } from '@/types/contact';

const MAX_CONTACT_FILE_SIZE_BYTES = 25 * 1024 * 1024;

function normalizeContactFile(
  raw: Record<string, unknown>,
  type: ContactFileAttachment['type'],
): ContactFileAttachment {
  return {
    id: String(raw.id ?? raw._id ?? ''),
    fileName: String(raw.fileName ?? raw.filename ?? 'Attachment'),
    mimeType: String(raw.mimeType ?? raw.contentType ?? 'application/octet-stream'),
    fileSize: Number(raw.fileSize ?? raw.sizeBytes ?? 0),
    uploadedBy: raw.uploadedBy ? String(raw.uploadedBy) : null,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    type,
  };
}

function sortByNewest(a: ContactFileAttachment, b: ContactFileAttachment) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function isImageFile(fileOrMimeType: File | string) {
  const mimeType = typeof fileOrMimeType === 'string' ? fileOrMimeType : fileOrMimeType.type;
  return mimeType.startsWith('image/');
}

export function isPreviewableContactFile(file: ContactFileAttachment) {
  return file.mimeType.startsWith('image/') || file.mimeType === 'application/pdf';
}

export async function getContactFileDownloadUrl(contactId: string, file: ContactFileAttachment) {
  const endpoint =
    file.type === 'photo'
      ? `/contacts/${contactId}/photos/${file.id}/download-url`
      : `/contacts/${contactId}/attachments/${file.id}/download-url`;
  const raw = await apiGet<Record<string, unknown>>(operationsClient, endpoint);
  return String(raw.downloadUrl ?? raw.url ?? '');
}

export function useContactFiles(contactId: string) {
  return useApiQuery<ContactFileAttachment[]>({
    queryKey: contactKeys.files(contactId),
    queryFn: async () => {
      const [attachmentsRaw, photosRaw] = await Promise.all([
        apiGet<Record<string, unknown>[]>(operationsClient, `/contacts/${contactId}/attachments`),
        apiGet<Record<string, unknown>[]>(operationsClient, `/contacts/${contactId}/photos`),
      ]);

      const attachments = attachmentsRaw.map((raw) => normalizeContactFile(raw, 'attachment'));
      const photos = photosRaw.map((raw) => normalizeContactFile(raw, 'photo'));

      return [...attachments, ...photos].sort(sortByNewest);
    },
    enabled: Boolean(contactId),
    showErrorToast: false,
  });
}

export function useUploadContactFile(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ContactFileAttachment, File>({
    mutationFn: async (file) => {
      if (file.size > MAX_CONTACT_FILE_SIZE_BYTES) {
        throw new Error('File exceeds 25 MB limit');
      }

      const type: ContactFileAttachment['type'] = isImageFile(file) ? 'photo' : 'attachment';
      const uploadEndpoint =
        type === 'photo'
          ? `/contacts/${contactId}/photos/upload-url`
          : `/contacts/${contactId}/attachments/upload-url`;

      const raw = await apiPost<Record<string, unknown>>(operationsClient, uploadEndpoint, {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      });

      const uploadUrl = String(raw.uploadUrl ?? '');
      const attachment = normalizeContactFile(
        (raw.attachment ?? {}) as Record<string, unknown>,
        type,
      );

      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }

      return attachment;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.files(contactId) });
      toast.success('Attachment uploaded');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to upload attachment');
    },
  });
}

export function useDeleteContactFile(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<{ deleted: boolean }, ContactFileAttachment>({
    mutationFn: (file) => {
      const endpoint =
        file.type === 'photo'
          ? `/contacts/${contactId}/photos/${file.id}`
          : `/contacts/${contactId}/attachments/${file.id}`;
      return apiDelete<{ deleted: boolean }>(operationsClient, endpoint);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.files(contactId) });
      toast.success('Attachment deleted');
    },
  });
}
